// services/mmaService.js
class MMAService {
  constructor() {
    this.baseURL = 'https://v1.mma.api-sports.io';
    this.apiKey = process.env.REACT_APP_FOOTBALL_API_KEY;
    this.debug = false;
    this.cache = {};

    if (!this.apiKey) {
      console.warn('MMA API key not found in environment variables');
    }
  }

  // Method to manually set API key if env vars aren't working
  setApiKey(key) {
    this.apiKey = key;
    this.log('API key manually set:', { keyLength: key ? key.length : 0 }); 
  }

  log(message, data = null) {
    if (this.debug) {
      console.log(`[MMA API] ${message}`, data || '');
    }
  }

  // Cache management
  getCacheKey(endpoint, params) {
    return `mma_${endpoint}_${JSON.stringify(params)}`;
  }

  setCache(key, data, ttl = 300000) { // 5 minutes default TTL
    this.cache[key] = {
      data,
      timestamp: Date.now(),
      ttl
    };
  }

  getCache(key) {
    const cached = this.cache[key];
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > cached.ttl) {
      delete this.cache[key];
      return null;
    }
    
    return cached.data;
  }

  clearCache() {
    this.cache = {};
    this.log('Cache cleared');
  }

  async makeRequest(endpoint, params = {}) {
    // Check cache first
    const cacheKey = this.getCacheKey(endpoint, params);
    const cachedData = this.getCache(cacheKey);
    if (cachedData) {
      this.log(`Cache hit for ${endpoint}`, params);
      return cachedData;
    }

    this.log(`Making request to ${endpoint}`, params);
    
    if (!this.apiKey) {
      this.log('API Key Debug Info:', {
        mmaKey: process.env.REACT_APP_MMA_API_KEY,
        footballKey: process.env.REACT_APP_FOOTBALL_API_KEY,
        allReactAppVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
      });
      throw new Error(`MMA API key not configured.
Neither REACT_APP_MMA_API_KEY nor REACT_APP_FOOTBALL_API_KEY found.
Available REACT_APP_ variables: ${Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')).join(', ')}

Please:
1. Add REACT_APP_MMA_API_KEY or REACT_APP_FOOTBALL_API_KEY to your .env file
2. Restart your development server after adding the variable
3. Or use MMAService.setApiKey('your_key_here') as a temporary workaround`);
    }

    const url = new URL(`${this.baseURL}${endpoint}`);
    
    // Add query parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        url.searchParams.append(key, params[key]);
      }
    });

    this.log(`Request URL: ${url.toString()}`);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'x-rapidapi-host': 'v1.mma.api-sports.io',
          'x-rapidapi-key': this.apiKey,
          'Content-Type': 'application/json'
        }
      });

      this.log(`Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        this.log(`Error response:`, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      this.log(`Response data:`, data);

      // Check for API errors in response
      if (data.errors && data.errors.length > 0) {
        this.log(`API errors:`, data.errors);
        throw new Error(`API Error: ${data.errors.join(', ')}`);
      }

      // Cache successful responses with different TTLs based on data type
      let ttl = 300000; // 5 minutes default
      if (endpoint.includes('/fights') && !endpoint.includes('/statistics')) ttl = 30000; // 30 seconds for live fights
      if (endpoint.includes('/odds')) ttl = 120000; // 2 minutes for odds
      if (endpoint.includes('/categories') || endpoint.includes('/teams')) ttl = 86400000; // 24 hours for static data

      this.setCache(cacheKey, data, ttl);
      return data;
    } catch (error) {
      this.log(`Request failed:`, error.message);
      console.error('MMA API request failed:', error);
      throw error;
    }
  }

  // CORE ENDPOINTS

  // Get available seasons
  async getSeasons() {
    return await this.makeRequest('/seasons');
  }

  // Get available timezones
  async getTimezones() {
    return await this.makeRequest('/timezone');
  }

  // Get categories with proper validation
  async getCategories(search = null) {
    const params = {};
    if (search) {
      // Validate search parameter length (API requires >= 3 characters)
      if (search.length < 3) {
        throw new Error('search parameter must be at least 3 characters long');
      }
      params.search = search;
    }

    return await this.makeRequest('/categories', params);
  }

  // Get teams with proper validation
  async getTeams(id = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (search) {
      // Validate search parameter length (API requires >= 3 characters)
      if (search.length < 3) {
        throw new Error('search parameter must be at least 3 characters long');
      }
      params.search = search;
    }

    return await this.makeRequest('/teams', params);
  }

  // Get fighters with enhanced validation and parameter handling
  async getFighters(id = null, name = null, category = null, search = null, team = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (category) params.category = category;
    if (search) {
      // Validate search parameter length (API requires >= 3 characters)
      if (search.length < 3) {
        throw new Error('search parameter must be at least 3 characters long');
      }
      params.search = search;
    }
    if (team) params.team = team;

    // API requires at least one of: id, team, name, category, search
    if (!id && !team && !name && !category && !search) {
      throw new Error('getFighters requires at least one parameter: id, team, name, category, or search');
    }

    return await this.makeRequest('/fighters', params);
  }

  // Get fighter records
  async getFighterRecords(fighterId) {
    if (!fighterId) {
      throw new Error('Fighter ID is required for getFighterRecords');
    }
    return await this.makeRequest('/fighters/records', { id: fighterId });
  }

  // Get fights with improved efficiency and proper validation
  async getFights(id = null, date = null, fighter = null, category = null, season = null, timezone = 'America/New_York') {
    const params = {};
    
    if (id) params.id = id;
    if (date) {
      // Validate date format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        throw new Error('date parameter must be in YYYY-MM-DD format');
      }
      params.date = date;
    }
    if (fighter) params.fighter = fighter;
    if (category) params.category = category;
    if (season) {
      // Validate season format
      if (!/^\d{4}$/.test(season.toString()) || season < 2020 || season > new Date().getFullYear() + 1) {
        throw new Error('season parameter must be a valid 4-digit year');
      }
      params.season = season;
    }
    if (timezone) params.timezone = timezone;

    // API requires at least one of: id, date, season, fighter
    if (!id && !date && !season && !fighter) {
      // Default to current season
      params.season = new Date().getFullYear();
      this.log(`No required parameters provided, defaulting to current season ${params.season}`);
    }

    return await this.makeRequest('/fights', params);
  }

  // Get fight results with proper validation
  async getFightResults(id = null, ids = null, date = null) {
    const params = {};
    if (id) params.id = id;
    if (ids) {
      // Validate ids format (maximum of 10 fight ids in format "id-id-id")
      if (typeof ids === 'string' && ids.split('-').length > 10) {
        throw new Error('ids parameter supports maximum of 10 fight ids');
      }
      params.ids = ids;
    }
    if (date) {
      // Validate date format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        throw new Error('date parameter must be in YYYY-MM-DD format');
      }
      params.date = date;
    }

    // API requires at least one of: id, ids, date
    if (!id && !ids && !date) {
      throw new Error('getFightResults requires at least one parameter: id, ids, or date');
    }

    return await this.makeRequest('/fights/results', params);
  }

  // Get fighter statistics with proper validation
  async getFighterStatistics(id = null, ids = null, date = null, fighter = null) {
    const params = {};
    if (id) params.id = id;
    if (ids) {
      // Validate ids format (maximum of 10 fight ids)
      if (typeof ids === 'string' && ids.split('-').length > 10) {
        throw new Error('ids parameter supports maximum of 10 fight ids');
      }
      params.ids = ids;
    }
    if (date) {
      // Validate date format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        throw new Error('date parameter must be in YYYY-MM-DD format');
      }
      params.date = date;
    }
    if (fighter) params.fighter = fighter;

    // API requires at least one of: id, ids, date, fighter
    if (!id && !ids && !date && !fighter) {
      throw new Error('getFighterStatistics requires at least one parameter: id, ids, date, or fighter');
    }

    return await this.makeRequest('/fights/statistics/fighters', params);
  }

  // Get odds with proper validation
  async getOdds(fight = null, date = null, bookmaker = null, bet = null) {
    const params = {};
    if (fight) params.fight = fight;
    if (date) {
      // Validate date format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        throw new Error('date parameter must be in YYYY-MM-DD format');
      }
      params.date = date;
    }
    if (bookmaker) params.bookmaker = bookmaker;
    if (bet) params.bet = bet;

    // API requires at least one of: fight, date
    if (!fight && !date) {
      throw new Error('getOdds requires at least one parameter: fight or date');
    }

    return await this.makeRequest('/odds', params);
  }

  // Get bets with proper validation
  async getBets(id = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (search) {
      // Validate search parameter length (API requires >= 3 characters)
      if (search.length < 3) {
        throw new Error('search parameter must be at least 3 characters long');
      }
      params.search = search;
    }

    return await this.makeRequest('/odds/bets', params);
  }

  // Get bookmakers with proper validation
  async getBookmakers(id = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (search) {
      // Validate search parameter length (API requires >= 3 characters)
      if (search.length < 3) {
        throw new Error('search parameter must be at least 3 characters long');
      }
      params.search = search;
    }

    return await this.makeRequest('/odds/bookmakers', params);
  }

  // ENHANCED UTILITY METHODS

  // Get comprehensive fight card for a specific date
  async getFightCard(date, timezone = 'America/New_York') {
    try {
      this.log(`Getting complete fight card for ${date}`);
      
      const fights = await this.getFights(null, date, null, null, null, timezone);
      
      if (!fights.response || fights.response.length === 0) {
        return {
          date,
          fights: [],
          totalFights: 0,
          hasMainEvent: false,
          isEmpty: true
        };
      }

      // Sort fights by importance/order if available
      const sortedFights = fights.response.sort((a, b) => {
        // Sort by fight order or time if available
        if (a.time && b.time) {
          return new Date(`${date} ${a.time}`) - new Date(`${date} ${b.time}`);
        }
        return 0;
      });

      return {
        date,
        fights: sortedFights,
        totalFights: sortedFights.length,
        hasMainEvent: sortedFights.length > 0,
        isEmpty: false,
        formattedDate: this.formatDateET(`${date}T12:00:00Z`)
      };
    } catch (error) {
      this.log(`Error getting fight card for ${date}:`, error.message);
      throw error;
    }
  }

  // Get comprehensive fighter profile with recent fights and stats
  async getComprehensiveFighterProfile(fighterId) {
    try {
      this.log(`Getting comprehensive profile for fighter ${fighterId}`);
      
      const [
        fighterInfo,
        fighterRecords,
        recentFights
      ] = await Promise.allSettled([
        this.getFighters(fighterId),
        this.getFighterRecords(fighterId),
        this.getFighterRecentFights(fighterId, 5)
      ]);

      const profile = {
        fighter: fighterInfo.status === 'fulfilled' ? fighterInfo.value.response?.[0] : null,
        records: fighterRecords.status === 'fulfilled' ? fighterRecords.value.response : null,
        recentFights: recentFights.status === 'fulfilled' ? recentFights.value.response : [],
        lastUpdated: new Date().toISOString()
      };

      // Calculate additional stats
      if (profile.recentFights && profile.recentFights.length > 0) {
        profile.recentPerformance = this.calculateRecentPerformance(profile.recentFights, fighterId);
      }

      return profile;
    } catch (error) {
      this.log(`Error getting comprehensive fighter profile for ${fighterId}:`, error.message);
      throw error;
    }
  }

  // Enhanced fighter historical stats and records
  async getFighterHistoricalStats(fighterId) {
    if (!fighterId) {
      throw new Error('Fighter ID is required for getFighterHistoricalStats');
    }

    try {
      this.log(`Getting comprehensive historical stats for fighter ${fighterId}`);
      
      // Get fighter basic info, records, and fight history in parallel
      const [fighterInfo, fighterRecords, recentFights] = await Promise.allSettled([
        this.getFighters(fighterId),
        this.getFighterRecords(fighterId),
        this.getFighterRecentFights(fighterId)
      ]);

      const result = {
        fighter: fighterInfo.status === 'fulfilled' ? fighterInfo.value.response?.[0] : null,
        records: fighterRecords.status === 'fulfilled' ? fighterRecords.value.response : null,
        recentFights: recentFights.status === 'fulfilled' ? recentFights.value.response : [],
        lastUpdated: new Date().toISOString()
      };

      this.log(`Historical stats compiled for fighter ${fighterId}:`, result);
      return result;
    } catch (error) {
      this.log(`Error getting historical stats for fighter ${fighterId}:`, error.message);
      throw error;
    }
  }

  // Get fighter's recent fights for historical analysis
  async getFighterRecentFights(fighterId, limit = 10) {
    if (!fighterId) {
      throw new Error('Fighter ID is required for getFighterRecentFights');
    }

    try {
      // Try to get fights where this fighter participated
      const currentYear = new Date().getFullYear();
      const seasons = [currentYear, currentYear - 1, currentYear - 2]; // Last 3 seasons
      
      let allFights = [];
      
      for (const season of seasons) {
        try {
          const seasonFights = await this.makeRequest('/fights', { 
            season: season,
            fighter: fighterId 
          });
          
          if (seasonFights.response && seasonFights.response.length > 0) {
            allFights = allFights.concat(seasonFights.response);
          }
        } catch (seasonError) {
          this.log(`No fights found for fighter ${fighterId} in season ${season}`);
          continue;
        }
      }

      // Sort by date (most recent first) and limit results
      allFights.sort((a, b) => new Date(b.date) - new Date(a.date));
      const recentFights = allFights.slice(0, limit);

      this.log(`Found ${recentFights.length} recent fights for fighter ${fighterId}`);
      return { response: recentFights };
    } catch (error) {
      this.log(`Error getting recent fights for fighter ${fighterId}:`, error.message);
      return { response: [] };
    }
  }

  // Calculate fighter's recent performance metrics
  calculateRecentPerformance(recentFights, fighterId) {
    const performance = {
      totalRecentFights: recentFights.length,
      wins: 0,
      losses: 0,
      winStreak: 0,
      lastFightDate: null,
      finishRate: 0,
      averageFightTime: null
    };

    let currentStreak = 0;
    let finishes = 0;
    
    // Sort fights by date (most recent first)
    const sortedFights = recentFights.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    sortedFights.forEach((fight, index) => {
      if (index === 0) {
        performance.lastFightDate = fight.date;
      }

      // Determine if fighter won
      const isWinner = this.didFighterWin(fight, fighterId);
      
      if (isWinner) {
        performance.wins++;
        if (index === 0 || currentStreak >= 0) {
          currentStreak++;
        }
      } else if (fight.status?.short === 'FT') {
        performance.losses++;
        if (currentStreak > 0) {
          currentStreak = 0;
        } else {
          currentStreak--;
        }
      }

      // Check for finish (non-decision)
      if (fight.method && !fight.method.toLowerCase().includes('decision')) {
        finishes++;
      }
    });

    performance.winStreak = currentStreak;
    performance.finishRate = performance.totalRecentFights > 0 ? 
      Math.round((finishes / performance.totalRecentFights) * 100) : 0;

    return performance;
  }

  // Helper method to determine if a fighter won a specific fight
  didFighterWin(fight, fighterId) {
    if (!fight.fighters) return false;
    
    const fighter = fight.fighters.first?.id === parseInt(fighterId) ? fight.fighters.first : 
                   fight.fighters.second?.id === parseInt(fighterId) ? fight.fighters.second : null;
    
    return fighter?.winner === true;
  }

  // Get detailed fighter statistics with performance analysis
  async getFighterPerformanceStats(fighterId) {
    if (!fighterId) {
      throw new Error('Fighter ID is required for getFighterPerformanceStats');
    }

    try {
      const historicalData = await this.getFighterHistoricalStats(fighterId);
      
      if (!historicalData.recentFights || historicalData.recentFights.length === 0) {
        return {
          fighter: historicalData.fighter,
          performance: {
            totalFights: 0,
            wins: 0,
            losses: 0,
            winRate: 0,
            recentForm: 'No recent fights'
          }
        };
      }

      // Analyze recent performance
      const recentFights = historicalData.recentFights;
      let wins = 0;
      let losses = 0;
      
      recentFights.forEach(fight => {
        if (fight.fighters) {
          // Check if this fighter won
          const fighter = fight.fighters.first?.id === parseInt(fighterId) ? fight.fighters.first : 
                         fight.fighters.second?.id === parseInt(fighterId) ? fight.fighters.second : null;
          
          if (fighter?.winner) {
            wins++;
          } else if (fight.status?.short === 'FT') { // Fight finished but fighter didn't win
            losses++;
          }
        }
      });

      const totalFights = wins + losses;
      const winRate = totalFights > 0 ? (wins / totalFights * 100).toFixed(1) : 0;
      
      // Recent form (last 5 fights)
      const last5 = recentFights.slice(0, 5);
      const recentForm = last5.map(fight => {
        if (fight.fighters) {
          const fighter = fight.fighters.first?.id === parseInt(fighterId) ? fight.fighters.first : 
                         fight.fighters.second?.id === parseInt(fighterId) ? fight.fighters.second : null;
          return fighter?.winner ? 'W' : (fight.status?.short === 'FT' ? 'L' : 'N/A');
        }
        return 'N/A';
      }).join('-');

      return {
        fighter: historicalData.fighter,
        records: historicalData.records,
        performance: {
          totalFights,
          wins,
          losses,
          winRate: parseFloat(winRate),
          recentForm: recentForm || 'N/A',
          last5Fights: last5
        },
        lastUpdated: historicalData.lastUpdated
      };
    } catch (error) {
      this.log(`Error getting performance stats for fighter ${fighterId}:`, error.message);
      throw error;
    }
  }

  // Get upcoming fights for a date range
  async getUpcomingFights(days = 7, timezone = 'America/New_York') {
    try {
      this.log(`Getting upcoming fights for next ${days} days`);
      
      const upcomingDates = this.getUpcomingDates(days);
      const allFights = [];

      for (const dateObj of upcomingDates) {
        try {
          const dayFights = await this.getFights(null, dateObj.value, null, null, null, timezone);
          if (dayFights.response && dayFights.response.length > 0) {
            allFights.push({
              date: dateObj.value,
              dateLabel: dateObj.label,
              fights: dayFights.response,
              fightCount: dayFights.response.length
            });
          }
        } catch (error) {
          this.log(`No fights found for ${dateObj.value}`);
          continue;
        }
      }

      return {
        upcomingFights: allFights,
        totalDays: allFights.length,
        totalFights: allFights.reduce((sum, day) => sum + day.fightCount, 0),
        nextFightDate: allFights.length > 0 ? allFights[0].date : null
      };
    } catch (error) {
      this.log('Error getting upcoming fights:', error.message);
      throw error;
    }
  }

  // Search fighters by name with enhanced results
  async searchFighters(searchTerm, limit = 10) {
    try {
      if (searchTerm.length < 3) {
        throw new Error('Search term must be at least 3 characters long');
      }

      this.log(`Searching fighters with term: ${searchTerm}`);
      
      const results = await this.getFighters(null, null, null, searchTerm);
      
      if (!results.response) {
        return { fighters: [], total: 0, searchTerm };
      }

      // Enhance results with additional info
      const enhancedResults = results.response.slice(0, limit).map(fighter => ({
        ...fighter,
        searchRelevance: this.calculateSearchRelevance(fighter, searchTerm)
      }));

      // Sort by relevance
      enhancedResults.sort((a, b) => b.searchRelevance - a.searchRelevance);

      return {
        fighters: enhancedResults,
        total: results.response.length,
        searchTerm,
        hasMore: results.response.length > limit
      };
    } catch (error) {
      this.log(`Error searching fighters with term "${searchTerm}":`, error.message);
      throw error;
    }
  }

  // Calculate search relevance score for fighter results
  calculateSearchRelevance(fighter, searchTerm) {
    let score = 0;
    const term = searchTerm.toLowerCase();
    
    if (!fighter.name) return score;
    
    const name = fighter.name.toLowerCase();
    
    // Exact match gets highest score
    if (name === term) score += 100;
    
    // Name starts with search term
    if (name.startsWith(term)) score += 75;
    
    // Name contains search term
    if (name.includes(term)) score += 50;
    
    // Each word in name that starts with search term
    const nameWords = name.split(' ');
    nameWords.forEach(word => {
      if (word.startsWith(term)) score += 25;
    });
    
    return score;
  }

  // Get fight odds with analysis
  async getFightOddsWithAnalysis(fightId) {
    try {
      this.log(`Getting odds analysis for fight ${fightId}`);
      
      const odds = await this.getOdds(fightId);
      
      if (!odds.response || odds.response.length === 0) {
        return {
          fightId,
          hasOdds: false,
          odds: [],
          analysis: null
        };
      }

      const analysis = this.analyzeOdds(odds.response);
      
      return {
        fightId,
        hasOdds: true,
        odds: odds.response,
        analysis,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      this.log(`Error getting odds analysis for fight ${fightId}:`, error.message);
      throw error;
    }
  }

  // Analyze betting odds for insights
  analyzeOdds(oddsData) {
    const analysis = {
      totalBookmakers: oddsData.length,
      bestOdds: {},
      worstOdds: {},
      averageOdds: {},
      consensus: null
    };

    if (oddsData.length === 0) return analysis;

    // Group odds by fighter/outcome
    const oddsByOutcome = {};
    
    oddsData.forEach(bookmaker => {
      if (bookmaker.bets) {
        bookmaker.bets.forEach(bet => {
          if (bet.values) {
            bet.values.forEach(value => {
              const key = value.value || 'unknown';
              if (!oddsByOutcome[key]) {
                oddsByOutcome[key] = [];
              }
              oddsByOutcome[key].push({
                bookmaker: bookmaker.name || 'Unknown',
                odd: parseFloat(value.odd || 0)
              });
            });
          }
        });
      }
    });

    // Calculate best, worst, and average odds for each outcome
    Object.keys(oddsByOutcome).forEach(outcome => {
      const odds = oddsByOutcome[outcome];
      const oddValues = odds.map(o => o.odd).filter(o => o > 0);
      
      if (oddValues.length > 0) {
        analysis.bestOdds[outcome] = Math.max(...oddValues);
        analysis.worstOdds[outcome] = Math.min(...oddValues);
        analysis.averageOdds[outcome] = oddValues.reduce((sum, odd) => sum + odd, 0) / oddValues.length;
      }
    });

    // Determine consensus favorite
    const avgOdds = Object.entries(analysis.averageOdds);
    if (avgOdds.length > 0) {
      analysis.consensus = avgOdds.reduce((favorite, [outcome, odds]) => {
        return !favorite || odds < favorite.odds ? { outcome, odds } : favorite;
      }, null);
    }

    return analysis;
  }

  // Enhanced method to get past fight results with detailed analysis
  async getHistoricalFights(days = 30) {
    try {
      this.log(`Getting historical fights for past ${days} days`);
      
      const pastDates = this.getPastDates(days);
      const allResults = [];

      for (const dateObj of pastDates) {
        try {
          const dayResults = await this.getFightResults(null, null, dateObj.value);
          if (dayResults.response && dayResults.response.length > 0) {
            allResults.push({
              date: dateObj.value,
              dateLabel: dateObj.label,
              results: dayResults.response,
              resultCount: dayResults.response.length
            });
          }
        } catch (error) {
          continue;
        }
      }

      return {
        historicalResults: allResults,
        totalDays: allResults.length,
        totalFights: allResults.reduce((sum, day) => sum + day.resultCount, 0),
        mostRecentDate: allResults.length > 0 ? allResults[0].date : null
      };
    } catch (error) {
      this.log('Error getting historical fights:', error.message);
      throw error;
    }
  }

  // UTILITY FUNCTIONS

  // Get today's date in YYYY-MM-DD format
  getTodayDate() {
    return new Date().toISOString().split('T')[0];
  }

  // Get date range
  getDateRange(days = 7, startFromToday = true) {
    const dates = [];
    const startOffset = startFromToday ? 0 : -days;
    
    for (let i = startOffset; i < (startFromToday ? days : 0); i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  }

  // Get upcoming dates with labels
  getUpcomingDates(days = 7) {
    const dates = [];
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      let label;
      if (i === 0) label = 'Today';
      else if (i === 1) label = 'Tomorrow';
      else label = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      
      dates.push({ value: dateStr, label });
    }
    return dates;
  }

  // Get past dates for historical data
  getPastDates(days = 7) {
    const dates = [];
    for (let i = 1; i <= days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const label = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      dates.push({ value: dateStr, label });
    }
    return dates.reverse(); // Most recent first
  }

  // Enhanced timezone utilities for ET
  convertToET(utcDateString) {
    const date = new Date(utcDateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  formatDateET(utcDateString) {
    const date = new Date(utcDateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }

  getFullDateTimeET(utcDateString) {
    const date = new Date(utcDateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  // Get fight status meanings for UI display
  getFightStatusInfo(status) {
    const statusMap = {
      'NS': { 
        label: 'Not Started', 
        color: 'bg-gray-100 text-gray-800',
        description: 'Fight has not begun'
      },
      'IN': { 
        label: 'Intros', 
        color: 'bg-blue-100 text-blue-800',
        description: 'Fighter introductions'
      },
      'PF': { 
        label: 'Pre-fight', 
        color: 'bg-yellow-100 text-yellow-800',
        description: 'Pre-fight preparations'
      },
      'LIVE': { 
        label: 'In Progress', 
        color: 'bg-red-100 text-red-800',
        description: 'Fight is currently active'
      },
      'EOR': { 
        label: 'End of Round', 
        color: 'bg-orange-100 text-orange-800',
        description: 'Between rounds'
      },
      'FT': { 
        label: 'Finished', 
        color: 'bg-slate-100 text-slate-800',
        description: 'Fight has concluded'
      },
      'WO': { 
        label: 'Walkouts', 
        color: 'bg-purple-100 text-purple-800',
        description: 'Fighter walkouts'
      },
      'CANC': { 
        label: 'Cancelled', 
        color: 'bg-red-100 text-red-800',
        description: 'Fight cancelled and not rescheduled'
      },
      'PST': { 
        label: 'Postponed', 
        color: 'bg-yellow-100 text-yellow-800',
        description: 'Fight postponed, waiting for new date'
      }
    };
    
    return statusMap[status] || { 
      label: status, 
      color: 'bg-gray-100 text-gray-800',
      description: 'Unknown status'
    };
  }

  // Get weight class information
  getWeightClassInfo(category) {
    const weightClasses = {
      'Strawweight': { min: 0, max: 115, gender: 'Women' },
      'Flyweight': { min: 115, max: 125, gender: 'Both' },
      'Bantamweight': { min: 125, max: 135, gender: 'Both' },
      'Featherweight': { min: 135, max: 145, gender: 'Both' },
      'Lightweight': { min: 145, max: 155, gender: 'Both' },
      'Welterweight': { min: 155, max: 170, gender: 'Men' },
      'Middleweight': { min: 170, max: 185, gender: 'Men' },
      'Light Heavyweight': { min: 185, max: 205, gender: 'Men' },
      'Heavyweight': { min: 205, max: 265, gender: 'Men' },
      'Super Heavyweight': { min: 265, max: null, gender: 'Men' },
      'Women\'s Strawweight': { min: 0, max: 115, gender: 'Women' },
      'Women\'s Flyweight': { min: 115, max: 125, gender: 'Women' },
      'Women\'s Bantamweight': { min: 125, max: 135, gender: 'Women' },
      'Women\'s Featherweight': { min: 135, max: 145, gender: 'Women' },
      'Women\'s Lightweight': { min: 145, max: 155, gender: 'Women' }
    };

    return weightClasses[category] || { 
      min: null, 
      max: null, 
      gender: 'Unknown',
      isValid: false 
    };
  }

  // Test API connection
  async testConnection() {
    try {
      this.log('Testing API connection...');
      const categoriesResult = await this.getCategories();
      this.log('Categories test result:', categoriesResult);
      
      if (categoriesResult && categoriesResult.response) {
        this.log('✅ API connection successful');
        return { success: true, categories: categoriesResult.response };
      } else {
        throw new Error('Invalid response structure');
      }
    } catch (error) {
      this.log('❌ API connection failed:', error.message);
      return { success: false, error: error.message };
    }
  }

  // Get sample data for testing
  async getSampleData() {
    try {
      this.log('Getting sample data...');
      
      // Try to get categories first
      const categories = await this.getCategories();
      this.log('Sample categories:', categories);
      
      // Try to get seasons
      const seasons = await this.getSeasons();
      this.log('Sample seasons:', seasons);
      
      // Try to get some fights from the most recent season
      if (seasons.response && seasons.response.length > 0) {
        const latestSeason = Math.max(...seasons.response);
        this.log(`Trying to get fights from season ${latestSeason}`);
        
        const fights = await this.getFights(null, null, null, null, latestSeason);
        this.log('Sample fights:', fights);
        
        return {
          categories: categories.response || [],
          seasons: seasons.response || [],
          fights: fights.response || [],
          latestSeason
        };
      }
      
      return {
        categories: categories.response || [],
        seasons: seasons.response || [],
        fights: [],
        latestSeason: null
      };
      
    } catch (error) {
      this.log('Error getting sample data:', error.message);
      throw error;
    }
  }

  // Debug method to check environment setup
  debugEnvironment() {
    const envInfo = {
      nodeEnv: process.env.NODE_ENV,
      apiKey: this.apiKey ? `Set (${this.apiKey.length} chars)` : 'Not set',
      apiKeyRaw: process.env.REACT_APP_MMA_API_KEY,
      allReactAppVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')),
      envCount: Object.keys(process.env).length,
      cacheSize: Object.keys(this.cache).length
    };
    
    console.log('🔍 MMA Service Environment Debug:', envInfo);
    return envInfo;
  }
}

// Add a global method for easy testing in console
window.MMAService = new MMAService();

export default window.MMAService;